/*
  Two fields added to a beneficiary, and the one list of relationships both
  modals read.

  ---- why there is no second set of contact fields ----

  A minor is contacted through a guardian, but the questions are the same
  questions: an address to write to, a phone number, a date of birth, whether to
  notify now. So a minor uses the SAME fields as anyone else — `email`,
  `email_2`, `email_3`, `phone_number`, `birth_date`, `awareness_status` — and
  `is_minor` only changes what the form calls them.

  That is what makes the rest work for free. The access key is still
  email : birth_date : personal_code, so nothing about logging in changes and
  nothing branches. The additional-address button, the notify-on-switch rule,
  the validation and the re-keying on a password change all carry over untouched
  because there is nothing for them to carry over TO.

  `relationship` is asked of every beneficiary, minor or not — an adult sister is
  a sister — and is never required.
*/

/*
  Stored as a key, shown as a label.

  The value written to the record is the key, so a beneficiary entered in French
  and read in English says "Spouse" rather than staying "Conjoint(e)" — the
  record holds what the relationship IS, not the words one user happened to see.
*/
export const RELATIONSHIP_OPTIONS = [
  { value: "spouse", en: "Spouse", fr: "Conjoint(e)" },
  { value: "partner", en: "Partner", fr: "Partenaire" },
  { value: "son", en: "Son", fr: "Fils" },
  { value: "daughter", en: "Daughter", fr: "Fille" },
  { value: "grandchild", en: "Grandchild", fr: "Petit-enfant" },
  { value: "father", en: "Father", fr: "Père" },
  { value: "mother", en: "Mother", fr: "Mère" },
  { value: "brother", en: "Brother", fr: "Frère" },
  { value: "sister", en: "Sister", fr: "Sœur" },
  { value: "sibling", en: "Sibling", fr: "Frère / Sœur" },
  { value: "nephew", en: "Nephew", fr: "Neveu" },
  { value: "niece", en: "Niece", fr: "Nièce" },
  { value: "uncle", en: "Uncle", fr: "Oncle" },
  { value: "aunt", en: "Aunt", fr: "Tante" },
  { value: "cousin", en: "Cousin", fr: "Cousin / Cousine" },
  { value: "friend", en: "Friend", fr: "Ami(e)" },
  {
    value: "charity_organization",
    en: "Charity / Organization",
    fr: "Association / Organisation",
  },
  { value: "other", en: "Other", fr: "Autre" },
];

/*
  A stored key in the reader's language. An unrecognised key — one written by a
  later version, or hand-edited — is returned as it stands rather than blanked,
  so the record still says something.
*/
export const relationshipLabel = (value, lang) => {
  const found = RELATIONSHIP_OPTIONS.find((o) => o.value === value);
  if (!found) return value || "";
  return lang === "fr" ? found.fr : found.en;
};

/*
  What this feature added, and what each holds when unset. Spread into a new
  beneficiary, and used to fill in one saved before any of this existed — every
  such record reads as not-a-minor with no relationship, which is exactly right.
*/
export const ADDED_FIELDS = {
  is_minor: "false",
  relationship: "",
};

// A record that predates the feature has neither; it is not a minor.
export const withAddedFields = (beneficiary) => ({
  ...ADDED_FIELDS,
  ...(beneficiary || {}),
});

export const isMinor = (beneficiary) =>
  String(beneficiary?.is_minor) === "true";
